import { Box, Card, CardContent, Grid, Stack } from "@mui/material";
import React, { useCallback, useEffect, useMemo } from "react";
import { Controller, ControllerRenderProps } from "react-hook-form";
import {
  Button,
  NumericFormatterTextField,
  Typography,
} from "@platform/design-system";

import { useCarrierRuleSelectionQuery } from "../../../hooks/useBbe";
import { BrokerSummary, CarrierRuleSelection } from "../types";
import {
  CARRIER_ASSETS,
  CARRIER_NAME_MAP,
  CarrierBrokerSummary,
  CarrierName,
} from "../constants";
import { useBrokerSettingForm } from "../../../hooks/useBrokerSettingForm";

import { CarrierRuleSelect } from "./CarrierRuleSelect";
import { BonusSummaryDescription } from "./BonusSummaryDescription";

type BrokerSettingCardProps = {
  brokerSummary: CarrierBrokerSummary;
  refetch: () => void;
  onDirtyChange?: (carrierId: string, isDirty: boolean) => void;
  registerSaveHandler?: (
    carrierId: string,
    handler: () => Promise<void>,
  ) => void;
  setIsPending: (value: boolean) => void;
};

export const BrokerSettingCard: React.FC<BrokerSettingCardProps> = ({
  brokerSummary,
  refetch,
  onDirtyChange,
  registerSaveHandler,
  setIsPending,
}) => {
  const mappedCarrierName =
    CARRIER_NAME_MAP[brokerSummary.carrierName as CarrierName];

  const defaultCarrierBonusSummary =
    brokerSummary.brokerSummaries.find((s) => s.isCurrent) ??
    brokerSummary.brokerSummaries[0];
  const defaultRuleKey = defaultCarrierBonusSummary?.carrierRuleId;

  const {
    control,
    watch,
    setBrokerSummaryId,
    isPending,
    setCarrierRule,
    handleSave,
    carrierRule,
    isValid,
    isDirty,
    setValue,
  } = useBrokerSettingForm({
    brokerSummary: defaultCarrierBonusSummary,
    refetch,
  });

  const carrierRuleYear = carrierRule?.ruleYear;
  useEffect(() => setIsPending(isPending), [isPending, setIsPending]);

  useEffect(() => {
    onDirtyChange?.(brokerSummary.carrierId, isDirty);
  }, [isDirty, brokerSummary.carrierId, onDirtyChange]);

  useEffect(() => {
    registerSaveHandler?.(brokerSummary.carrierId, handleSave);
  }, [brokerSummary.carrierId, handleSave, registerSaveHandler]);

  const carrierRuleSelectionsQuery = useCarrierRuleSelectionQuery();
  const rulesByCarrier = useMemo(() => {
    if (!carrierRuleSelectionsQuery?.data) return [];

    return carrierRuleSelectionsQuery.data.filter(
      (rule) => rule.carrierId === brokerSummary.carrierId,
    );
  }, [brokerSummary, carrierRuleSelectionsQuery.data]);

  useEffect(() => {
    if (rulesByCarrier.length === 0) {
      setCarrierRule(null);
      return;
    }

    if (carrierRule !== null) return;

    if (defaultRuleKey) {
      const matchedRule = rulesByCarrier.find(
        (rule) => rule.carrierRuleId === defaultRuleKey,
      );

      if (matchedRule) {
        setCarrierRule(matchedRule);
        return;
      }
    }

    setCarrierRule(rulesByCarrier[0]);
  }, [
    rulesByCarrier,
    brokerSummary.brokerSummaries,
    defaultRuleKey,
    carrierRule,
    setCarrierRule,
  ]);

  const beginningBookSize = watch("beginningBookSize");

  const getRuleTitle = useMemo(() => {
    const currentRule = rulesByCarrier.find(
      (item) => item?.carrierRuleId === carrierRule?.carrierRuleId,
    );
    return currentRule?.ruleTitle ?? "No rule selected";
  }, [carrierRule?.carrierRuleId, rulesByCarrier]);

  const onChange = useCallback(
    (value: CarrierRuleSelection | null) => {
      setCarrierRule(value);
      let bonusSummary: BrokerSummary | null = null;

      if (value) {
        const res = brokerSummary?.brokerSummaries.find(
          (b) => b.carrierRuleId === value?.carrierRuleId,
        );
        bonusSummary = res ?? null;
      }

      const setValueOptions = { shouldDirty: true, shouldValidate: true };
      setValue(
        "paidBonus",
        bonusSummary?.previousYearBonus ?? 0,
        setValueOptions,
      );
      setValue("bonusGoal", bonusSummary?.bonusGoal ?? 0, setValueOptions);
      setValue("locCount", bonusSummary?.beginningLoc ?? 0, setValueOptions);
      setValue(
        "beginningBookSize",
        bonusSummary?.beginningBookSize ?? 0,
        setValueOptions,
      );
      setValue(
        "carrierRuleId",
        bonusSummary?.carrierRuleId ?? value?.carrierRuleId,
        setValueOptions,
      );

      setBrokerSummaryId(bonusSummary?.id);
    },
    [
      brokerSummary?.brokerSummaries,
      setBrokerSummaryId,
      setCarrierRule,
      setValue,
    ],
  );

  const normalizeInputValue = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<{
      paidBonus?: number;
      beginningBookSize?: number;
      locCount?: number;
      bonusGoal?: number;
      carrierRuleId?: string;
    }>,
  ): void =>
    field.onChange(e.target.value === "" ? "" : Number(e.target.value));

  if (
    !brokerSummary.brokerSummaries ||
    brokerSummary.brokerSummaries.length === 0
  ) {
    return null;
  }

  return (
    <Card>
      <CardContent style={{ padding: 24 }}>
        <Stack spacing={3}>
          <Stack spacing={0} direction="row" justifyContent="space-between">
            <div style={{ flex: 2 }}>
              <Box
                component="img"
                alt="carrier-logo"
                src={CARRIER_ASSETS[mappedCarrierName]}
              />
            </div>
            <CarrierRuleSelect
              carrierId={brokerSummary.carrierId}
              value={carrierRule}
              onChange={onChange}
              label="Carrier Rule Selection"
            />
          </Stack>

          <Stack spacing={3} direction="row">
            <Stack width="50%" spacing={3}>
              <Stack spacing={0}>
                <Typography variant="header-medium">Bonus Settings</Typography>
                <Typography variant="body-medium">
                  Plan Year {carrierRuleYear}
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="paidBonus"
                    control={control}
                    render={({ field }) => (
                      <NumericFormatterTextField
                        label={`${(carrierRuleYear ?? 0) - 1} Paid Bonus`}
                        fullWidth
                        value={field.value}
                        onChange={(e) => normalizeInputValue(e, field)}
                        fixedDecimalScale
                        decimalScale={0}
                        prefix={"$"}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="beginningBookSize"
                    control={control}
                    render={({ field }) => (
                      <NumericFormatterTextField
                        label="Start of Year Premium"
                        fullWidth
                        onChange={(e) => normalizeInputValue(e, field)}
                        value={field.value}
                        fixedDecimalScale
                        decimalScale={0}
                        prefix={"$"}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="bonusGoal"
                    control={control}
                    render={({ field }) => (
                      <NumericFormatterTextField
                        label={`${carrierRuleYear} Bonus Goal`}
                        fullWidth
                        value={field.value}
                        onChange={(e) => normalizeInputValue(e, field)}
                        fixedDecimalScale
                        decimalScale={0}
                        prefix={"$"}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="locCount"
                    control={control}
                    render={({ field }) => (
                      <NumericFormatterTextField
                        label="Start of Year LOC Count"
                        fullWidth
                        value={field.value}
                        onChange={(e) => normalizeInputValue(e, field)}
                        fixedDecimalScale
                        decimalScale={0}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={isPending || !carrierRule || !isValid}
                  loading={isPending}
                >
                  Save Settings
                </Button>
              </div>
            </Stack>

            <Stack width="50%" spacing={3}>
              <Typography variant="header-medium">
                Rules Summary: {getRuleTitle}
              </Typography>

              <BonusSummaryDescription
                carrierName={brokerSummary.carrierName as CarrierName}
                currentRuleId={carrierRule?.ruleId}
                startOfYearPremiumInput={Number(beginningBookSize) || 0}
                layout="grid"
                bonusYear={carrierRuleYear}
              />
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};
